import { ReactNode, forwardRef } from 'react';
import { Button as MuiButton, ButtonProps, SxProps, Theme, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import { ButtonTheme, ButtonVariant, buttonVariantMap } from './variants';
import Arrow from '@/assets/icons/arrow-right-white.svg';
import SmallArrow from '@/assets/icons/arrow-right-white-sm.svg';

interface IProps extends ButtonProps {
    children?: ReactNode;
    fixedEndIcon?: ReactNode;
    fixedStartIcon?: ReactNode | string;
    theme?: ButtonTheme;
    variant?: ButtonVariant;
    loading?: boolean;
    fontClassName?: string;
    showEndIcon?: boolean;
    testId?: string;
}

const Loading = () => {
    return (
        <CircularProgress
            color="inherit"
            data-testid="loading"
            sx={{
                position: 'absolute',
                width: '24px !important',
                height: '24px !important'
            }}
        />
    );
};

export const Button = forwardRef<HTMLButtonElement, IProps>(
    (
        {
            children,
            fixedStartIcon,
            fixedEndIcon,
            variant = 'contained',
            sx = {},
            theme = 'main',
            loading,
            showEndIcon,
            fontClassName = '',
            testId = '',
            disabled,
            ...props
        },
        ref
    ) => {
        const muiTheme = useTheme();
        const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

        return (
            <MuiButton
                ref={ref}
                data-testid={testId || "base-button"}
                {...props}
                variant={variant}
                disabled={disabled || loading}
                sx={
                    {
                        position: 'relative',
                        paddingTop: '9.5px',
                        paddingBottom: '9.5px',
                        fontSize: '18px',
                        ':hover': {
                            border: variant === 'contained' ? '2px solid #029780' : ''
                        },
                        [muiTheme.breakpoints.down('lg')]: {
                            paddingTop: '6.5px',
                            paddingBottom: '6.5px',
                            fontSize: '16px'
                        },
                        ...(buttonVariantMap[variant]?.[theme] || {}),
                        ...sx
                    } as SxProps<Theme>
                }
            >
                {fixedStartIcon && (
                    <span className="absolute left-[4%] top-1/2 -translate-y-1/2 lg:left-[6%]">
                        {typeof fixedStartIcon === 'string' ? <img src={fixedStartIcon} alt="" /> : fixedStartIcon}
                    </span>
                )}
                <span className={`${loading ? 'opacity-0' : ''} w-full font-dmSans ${fontClassName}`}>{children}</span>
                {loading && <Loading />}
                {showEndIcon && (
                    <span className="absolute right-[4%] top-1/2 -translate-y-1/2 lg:right-[6%]">
                        {fixedEndIcon ? (
                            fixedEndIcon
                        ) : (
                            <img src={isMobile ? SmallArrow : Arrow} alt="Arrow Right" className="lg-tablet:scale-50 tablet:scale-75" />
                        )}
                    </span>
                )}
            </MuiButton>
        );
    }
);

export default Button;
